// Small shared pieces for findings: severity badge, meta line, filter options.
import { Badge } from '@aurora-reactkit/components/badge';
import { EXPOSED_LABEL, PLACE_LABEL, SEVERITY_LABEL } from '../../domain/copy';
import { EXPOSED, exposesOf, PLACES, placeOf, type Exposed, type Place } from '../../domain/places';
import type { Finding, Severity } from '../../domain/types';

const SEV_VARIANT: Record<Severity, 'critical' | 'warning' | 'neutral'> = { critical: 'critical', high: 'critical', medium: 'warning', low: 'neutral' };

export function SeverityBadge({ severity }: { severity: Severity }) {
  return <Badge variant={SEV_VARIANT[severity]} appearance="subtle" size="sm" data-testid="severity-badge">{SEVERITY_LABEL[severity]}</Badge>;
}

export const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// Filters: severity, where we found it (place) and what it exposes. OR within a group, AND across groups.
export interface Filters { severity: Severity[]; place: Place[]; exposes: Exposed[] }
export const NO_FILTERS: Filters = { severity: [], place: [], exposes: [] };

export const matches = (f: Finding, fl: Filters) =>
  (!fl.severity.length || fl.severity.includes(f.severity)) && (!fl.place.length || fl.place.includes(placeOf(f)))
  && (!fl.exposes.length || exposesOf(f).some((k) => fl.exposes.includes(k)));

export function filterOptions(findings: Finding[]) {
  const sevOrder: Severity[] = ['critical', 'high', 'medium', 'low'];
  return {
    severity: sevOrder.filter((s) => findings.some((f) => f.severity === s)).map((v) => ({ value: v, label: SEVERITY_LABEL[v] })),
    place: PLACES.filter((p) => findings.some((f) => placeOf(f) === p)).map((v) => ({ value: v, label: PLACE_LABEL[v] })),
    exposes: EXPOSED.filter((k) => findings.some((f) => exposesOf(f).includes(k))).map((v) => ({ value: v, label: EXPOSED_LABEL[v] })),
  };
}

// Ask: canned answers that cite the evidence on the finding.
export function cannedAnswers(f: Finding, fixSummary: string, firstStep?: string) {
  const ev = f.evidence[0];
  const facts = ev ? Object.entries(ev.facts) : [];
  const fact = (k: string) => facts.find(([key]) => key === k)?.[1];
  const how = ev?.kind === 'breach'
    ? `${f.title.replace(/ \(\d{4}\)$/, '')} was breached on ${fmtDate(String(fact('breachDate')))}. ${fact('accountsAffected')} accounts were affected and the leaked data includes ${String(fact('dataExposed')).toLowerCase()}. Your details are in that data.`
    : ev?.kind === 'duplicate_charge'
      ? `${fact('merchant')} charged ${fact('currency')} ${fact('firstCharge')} and then ${fact('currency')} ${fact('secondCharge')} again ${fact('gapSeconds')} seconds later. A second identical charge that fast is usually a payment error, not a purchase.`
      : ev ? `We found this on ${fact('site') ?? fact('broker') ?? 'a public site'}: it shows ${String(fact('visible') ?? fact('shows') ?? 'details about you').toLowerCase()}.` : f.whatHappened;
  return [
    { q: 'How did this happen?', a: how, cites: ev?.title },
    { q: 'What should I do first?', a: firstStep ? `${fixSummary} Start with: ${firstStep}.` : fixSummary, cites: 'Fix for this finding' },
    { q: 'Is this confirmed?', a: f.confidence === 'confirmed' ? 'Yes. The record matches an identifier you verified.' : 'Not fully. The record looks like you, but we could not match it to a verified identifier. Dismiss it if it is not you.', cites: 'Confidence' },
  ];
}

// Where we found it, in the user's words: "Data breaches", "People-search sites".
export const sourceLine = (f: Finding) => PLACE_LABEL[placeOf(f)];
